import {
  Button,
  type ButtonProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react'
import React, { type ReactNode } from 'react'

export type ConfirmationDialogConfig = {
  body?: ReactNode
  confirmButtonProps?: ButtonProps
  confirmText?: string
  dismissButtonProps?: ButtonProps
  dismissText?: string
  header?: ReactNode
}

export type ConfirmationDialogProps = {
  isOpen: boolean
  onConfirm: () => void
  onDismiss: () => void
} & ConfirmationDialogConfig

export const ConfirmationDialog = ({
  body,
  confirmButtonProps,
  confirmText,
  dismissButtonProps,
  dismissText,
  header,
  isOpen,
  onConfirm,
  onDismiss,
}: Readonly<ConfirmationDialogProps>) => {
  return (
    <Modal
      classNames={{
        backdrop: 'z-[1024]',
        wrapper: 'z-[1025]',
      }}
      hideCloseButton
      isOpen={isOpen}
      placement="center"
      size="lg"
    >
      <ModalContent>
        {header && (
          <ModalHeader className="flex flex-col gap-1">{header}</ModalHeader>
        )}
        {body && <ModalBody>{body}</ModalBody>}
        <ModalFooter>
          <Button
            color="danger"
            onPress={onDismiss}
            variant="bordered"
            {...dismissButtonProps}
          >
            {dismissText || 'Cancel'}
          </Button>
          <Button color="primary" onPress={onConfirm} {...confirmButtonProps}>
            {confirmText || 'Confirm'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

type DialogConfig = {
  actionCallback: (confirmed: boolean) => void
} & ConfirmationDialogConfig

interface ConfirmationDialogProviderProps {
  children: ReactNode
}

const ConfirmationDialogContext = React.createContext<
  | {
      openDialog: (config: DialogConfig) => void
    }
  | undefined
>(undefined)

const ConfirmationDialogProvider: React.FC<ConfirmationDialogProviderProps> = ({
  children,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [dialogConfig, setDialogConfig] = React.useState<DialogConfig | null>(
    null,
  )

  const openDialog = ({
    actionCallback,
    body,
    confirmButtonProps,
    confirmText,
    dismissButtonProps,
    dismissText,
    header,
  }: DialogConfig) => {
    setDialogOpen(true)
    setDialogConfig({
      actionCallback,
      body,
      confirmButtonProps,
      confirmText,
      dismissButtonProps,
      dismissText,
      header,
    })
  }

  const resetDialog = () => {
    setDialogOpen(false)
    setDialogConfig(null)
  }

  const onConfirm = () => {
    if (dialogConfig) {
      dialogConfig.actionCallback(true)
      resetDialog()
    }
  }

  const onDismiss = () => {
    if (dialogConfig) {
      dialogConfig.actionCallback(false)
      resetDialog()
    }
  }

  return (
    <ConfirmationDialogContext.Provider value={{ openDialog }}>
      {dialogConfig && (
        <ConfirmationDialog
          body={dialogConfig.body}
          confirmButtonProps={dialogConfig.confirmButtonProps}
          confirmText={dialogConfig.confirmText}
          dismissButtonProps={dialogConfig.dismissButtonProps}
          dismissText={dialogConfig.dismissText}
          header={dialogConfig.header}
          isOpen={dialogOpen}
          onConfirm={onConfirm}
          onDismiss={onDismiss}
        />
      )}
      {children}
    </ConfirmationDialogContext.Provider>
  )
}

const useConfirmationDialog = () => {
  const context = React.useContext(ConfirmationDialogContext)

  if (!context) {
    throw new Error(
      'useConfirmationDialog must be used within a ConfirmationDialogProvider',
    )
  }

  const { openDialog } = context

  const getConfirmation = (options: Omit<DialogConfig, 'actionCallback'>) =>
    new Promise<boolean>((resolve) => {
      openDialog({ ...options, actionCallback: resolve })
    })

  return { getConfirmation }
}

export default ConfirmationDialog
export { ConfirmationDialogProvider, useConfirmationDialog }
